import { useEffect, useRef, useState } from "react";
import {
  ApiError,
  SESSION_EXPIRED_STORAGE_KEY,
  clearTokens,
  isAuthRefreshActive,
} from "../api/client";
import LoadingSpinner from "./LoadingSpinner";

interface ErrorFallbackProps {
  error?: unknown;
  /**
   * The error-boundary's reset callback (provided by `Sentry.ErrorBoundary`'s
   * fallback). When a caught error is a transient refresh-window artefact
   * (issue #2359) we retry the subtree by calling this once the silent refresh
   * has settled, so the target screen renders instead of the crash card.
   */
  resetError?: () => void;
}

/**
 * How long to keep polling for the silent refresh to settle before giving up on
 * a transient-recovery reset and showing the crash card. Bounds any pathological
 * loop where a component keeps throwing for a non-auth reason while a refresh
 * happens to stay active.
 */
const TRANSIENT_RECOVERY_TIMEOUT_MS = 6000;
const TRANSIENT_POLL_MS = 150;

/**
 * Detect whether the caught error (or ambient session state) indicates an
 * expired/invalid auth session rather than a genuine application crash.
 */
function isAuthError(error: unknown): boolean {
  // Direct 401 ApiError bubbling up through the component tree
  if (error instanceof ApiError && error.status === 401) {
    return true;
  }
  // The API client already flagged the session as expired before throwing
  if (sessionStorage.getItem(SESSION_EXPIRED_STORAGE_KEY)) {
    return true;
  }
  return false;
}

/**
 * True when the caught error is (almost certainly) a transient artefact of an
 * in-flight silent access-token refresh (issue #2359): a target route that
 * rendered against not-yet-resolved data during the refresh round-trip and threw
 * (e.g. a null-data `TypeError`). This is NOT a genuine auth failure -- the
 * refresh SUCCEEDED, so the session-expired flag is unset and `isAuthError`
 * returns false -- and must never surface the "Something went wrong" card.
 */
function isTransientRefreshError(error: unknown): boolean {
  if (isAuthError(error)) return false;
  return isAuthRefreshActive();
}

/**
 * Redirect to login, clearing tokens first so the login page shows the
 * session-expired banner (it checks SESSION_EXPIRED_STORAGE_KEY).
 */
function redirectToLogin(): void {
  clearTokens();
  // Ensure the session-expired flag is set so Login.tsx shows the banner
  sessionStorage.setItem(SESSION_EXPIRED_STORAGE_KEY, "1");
  window.location.href = "/login";
}

/**
 * Fallback UI displayed when an unhandled React error is caught
 * by the Sentry.ErrorBoundary wrapper.
 *
 * Three cases (issue #2359):
 *  1. Genuine auth failure (401 / session-expired flag) -> redirect to /login.
 *  2. Transient refresh-window error (a routine ~15-min / post-idle access-token
 *     refresh is in flight) -> show a quiet loading state and, once the refresh
 *     settles, reset the boundary so the target screen renders. The generic
 *     crash card is NEVER shown for this case.
 *  3. Genuine application crash -> the "Something went wrong" card + recovery.
 */
export default function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const [shouldRedirect] = useState(() => isAuthError(error));
  // Captured once at mount: is this a transient refresh-window error we should
  // silently recover rather than crash on?
  const [recovering, setRecovering] = useState(
    () => !!resetError && isTransientRefreshError(error),
  );
  const startedAt = useRef(Date.now());

  // Safety net: covers edge cases where the session-expired flag was set by
  // the API client just before the throw propagated, but the error prop itself
  // isn't an ApiError (e.g. a downstream TypeError from null user).
  useEffect(() => {
    if (shouldRedirect || sessionStorage.getItem(SESSION_EXPIRED_STORAGE_KEY)) {
      redirectToLogin();
    }
  }, [shouldRedirect]);

  // Transient recovery (#2359): while a silent refresh is active we hold a quiet
  // loading state; once it settles we reset the boundary ONCE so the subtree
  // re-renders against the now-refreshed data. If the refresh never settles
  // within the timeout, fall through to the crash card so a real fault is not
  // hidden forever.
  useEffect(() => {
    if (shouldRedirect || !recovering || !resetError) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      if (cancelled) return;
      if (!isAuthRefreshActive()) {
        // Refresh settled -> retry the target screen.
        resetError();
        return;
      }
      if (Date.now() - startedAt.current > TRANSIENT_RECOVERY_TIMEOUT_MS) {
        // Give up recovering silently -- surface the crash card.
        setRecovering(false);
        return;
      }
      timer = setTimeout(tick, TRANSIENT_POLL_MS);
    };
    timer = setTimeout(tick, TRANSIENT_POLL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [recovering, resetError, shouldRedirect]);

  // While redirecting to login, render nothing.
  if (shouldRedirect) {
    return null;
  }

  // While silently recovering a refresh-window transient, show the same quiet
  // loading state the app uses elsewhere -- never the crash card.
  if (recovering) {
    return <LoadingSpinner />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream dark:bg-slate-900 p-6">
      <div className="max-w-md rounded-lg bg-white dark:bg-slate-800 p-8 shadow-lg text-center">
        <h1 className="text-2xl font-semibold text-navy dark:text-slate-100 mb-4">
          Something went wrong
        </h1>
        <p className="text-text-light dark:text-slate-300 mb-6">
          An unexpected error occurred. The error has been reported and our team
          will look into it. Please try refreshing the page.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="rounded-md bg-red px-6 py-2 text-white font-medium hover:bg-red-light transition-colors"
        >
          Refresh Page
        </button>
      </div>
    </div>
  );
}
